import { useCallback, useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useUser';
import {
  getProjectTeamErrorMessage,
  projectTeamApi,
  type TradePartnersResponse,
} from '../api/api';
import type {
  AddInternalUserFormData,
  DeleteUsersPayload,
  InviteExternalUserFormData,
  InviteInternalUserFormData,
  ProjectTeamData,
  TeamMemberRow,
  Trade,
  TradePartnerApplication,
  TradePartnerApplicationSubmitInput,
  TradePartnerInput,
} from '../types/types';

export const projectTeamKeys = {
  all: ['project-team'] as const,
  team: (clientName: string, projectName: string) =>
    [...projectTeamKeys.all, 'team', clientName, projectName] as const,
  availableUsers: (clientId: number, projectId: number) =>
    [...projectTeamKeys.all, 'available-users', clientId, projectId] as const,
  tradePartners: (projectId: number, clientId: number) =>
    [...projectTeamKeys.all, 'trade-partners', projectId, clientId] as const,
  application: (token: string) =>
    [...projectTeamKeys.all, 'application', token] as const,
  invitation: (token: string) =>
    [...projectTeamKeys.all, 'invitation', token] as const,
};

type TeamSnapshot = { previous?: ProjectTeamData };

const invalidateProjectTeam = (
  queryClient: QueryClient,
  clientName?: string,
  projectName?: string,
) => {
  if (clientName && projectName) {
    return queryClient.invalidateQueries({
      queryKey: projectTeamKeys.team(clientName, projectName),
    });
  }
  return queryClient.invalidateQueries({ queryKey: projectTeamKeys.all });
};

export const useProjectTeam = () => {
  const { clientName = '', projectName = '' } = useParams<{
    clientName: string;
    projectName: string;
  }>();
  const { user: currentUser } = useUser();
  const queryClient = useQueryClient();
  const teamKey = projectTeamKeys.team(clientName, projectName);

  const teamQuery = useQuery({
    queryKey: teamKey,
    queryFn: () => projectTeamApi.getProjectTeam(clientName, projectName),
    enabled: Boolean(clientName && projectName),
    staleTime: 30_000,
  });

  const data = teamQuery.data;
  const [success, setSuccess] = useState<string | null>(null);
  const [localError, setError] = useState<string | null>(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formType, setFormType] = useState<'internal' | 'external'>('internal');
  const [addInternalUserForm, setAddInternalUserForm] = useState<AddInternalUserFormData>({
    user_id: '',
    role_id: '',
  });
  const [inviteInternalUserForm, setInviteInternalUserForm] =
    useState<InviteInternalUserFormData>({ email: '', role_id: '' });
  const [inviteExternalUserForm, setInviteExternalUserForm] =
    useState<InviteExternalUserFormData>({
      role_id: '',
      external_email: '',
      external_company_name: '',
    });
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const availableUsersQuery = useQuery({
    queryKey: projectTeamKeys.availableUsers(data?.client.id ?? 0, data?.project.id ?? 0),
    queryFn: () => projectTeamApi.getClientUsers(data!.client.id, data!.project.id),
    enabled: Boolean(
      isAddUserModalOpen &&
        formType === 'internal' &&
        data?.client.id &&
        data?.project.id,
    ),
    staleTime: 30_000,
  });

  const snapshotTeam = async (): Promise<TeamSnapshot> => {
    await queryClient.cancelQueries({ queryKey: teamKey });
    return { previous: queryClient.getQueryData<ProjectTeamData>(teamKey) };
  };

  const rollbackTeam = (_error: unknown, _variables: unknown, context?: TeamSnapshot) => {
    if (context?.previous) queryClient.setQueryData(teamKey, context.previous);
  };

  const settleTeam = () => invalidateProjectTeam(queryClient, clientName, projectName);

  const addUserMutation = useMutation({
    mutationFn: projectTeamApi.addUserToProject,
    onMutate: snapshotTeam,
    onError: rollbackTeam,
    onSettled: settleTeam,
  });

  const inviteInternalMutation = useMutation({
    mutationFn: projectTeamApi.inviteInternalUser,
    onMutate: snapshotTeam,
    onError: rollbackTeam,
    onSettled: settleTeam,
  });

  const inviteExternalMutation = useMutation({
    mutationFn: projectTeamApi.inviteExternalUser,
    onMutate: snapshotTeam,
    onError: rollbackTeam,
    onSettled: settleTeam,
  });

  const deleteUsersMutation = useMutation({
    mutationFn: projectTeamApi.deleteUsers,
    onMutate: async (payload: DeleteUsersPayload): Promise<TeamSnapshot> => {
      const context = await snapshotTeam();
      const deleted = new Set(payload.records.map((record) => `${record.type}-${record.id}`));
      queryClient.setQueryData<ProjectTeamData>(teamKey, (current) =>
        current
          ? {
              ...current,
              memberships: current.memberships.filter(
                (membership) => !deleted.has(`membership-${membership.id}`),
              ),
              invited_users: current.invited_users.filter(
                (invite) => !deleted.has(`invited_user-${invite.id}`),
              ),
            }
          : current,
      );
      return context;
    },
    onError: rollbackTeam,
    onSettled: settleTeam,
  });

  const handleAddInternalUser = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!data?.project.id) return;
      setError(null);
      try {
        await addUserMutation.mutateAsync({
          project_id: data.project.id,
          user_id: Number(addInternalUserForm.user_id),
          role_id: Number(addInternalUserForm.role_id),
          client_name: currentUser?.is_staff ? clientName : undefined,
        });
        setSuccess('User added successfully');
        setIsAddUserModalOpen(false);
        setAddInternalUserForm({ user_id: '', role_id: '' });
      } catch (error) {
        setError(getProjectTeamErrorMessage(error, 'Failed to add user'));
      }
    },
    [addInternalUserForm, addUserMutation, clientName, currentUser?.is_staff, data?.project.id],
  );

  const handleInviteInternalUser = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!data?.project.id) return;
      setError(null);
      try {
        const result = await inviteInternalMutation.mutateAsync({
          project_id: data.project.id,
          email: inviteInternalUserForm.email,
          role_id: Number(inviteInternalUserForm.role_id),
          client_id: currentUser?.is_staff ? data.client.id : undefined,
        });
        if (!result.success) throw new Error(result.error || 'Failed to send invitation');
        setSuccess('Invitation sent successfully');
        setIsAddUserModalOpen(false);
        setInviteInternalUserForm({ email: '', role_id: '' });
      } catch (error) {
        setError(getProjectTeamErrorMessage(error, 'Failed to send invitation'));
      }
    },
    [
      currentUser?.is_staff,
      data?.client.id,
      data?.project.id,
      inviteInternalMutation,
      inviteInternalUserForm,
    ],
  );

  const handleInviteExternalUser = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!data?.project.id) return;
      setError(null);
      try {
        await inviteExternalMutation.mutateAsync({
          project_id: data.project.id,
          external_email: inviteExternalUserForm.external_email,
          external_company_name: inviteExternalUserForm.external_company_name,
          role_id: Number(inviteExternalUserForm.role_id),
          client_name: currentUser?.is_staff ? clientName : undefined,
        });
        setSuccess('External user invited successfully');
        setIsAddUserModalOpen(false);
        setInviteExternalUserForm({
          role_id: '',
          external_email: '',
          external_company_name: '',
        });
      } catch (error) {
        setError(getProjectTeamErrorMessage(error, 'Failed to invite external user'));
      }
    },
    [
      clientName,
      currentUser?.is_staff,
      data?.project.id,
      inviteExternalMutation,
      inviteExternalUserForm,
    ],
  );

  const handleDeleteUsers = useCallback(async () => {
    if (!data?.project.id || selectedUsers.size === 0) return;
    setError(null);
    try {
      const records = Array.from(selectedUsers).map((key) => {
        const [type, id] = key.split('-');
        return {
          type: type as 'membership' | 'invited_user',
          id: Number(id),
        };
      });
      await deleteUsersMutation.mutateAsync({ project_id: data.project.id, records });
      setSuccess('Users deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedUsers(new Set());
    } catch (error) {
      setError(getProjectTeamErrorMessage(error, 'Failed to delete users'));
    }
  }, [data?.project.id, deleteUsersMutation, selectedUsers]);

  const teamMemberRows = useMemo<TeamMemberRow[]>(() => {
    if (!data) return [];
    const rows: TeamMemberRow[] = data.memberships.map((membership) => ({
      id: membership.id,
      type: 'membership',
      role: membership.role.name,
      roleId: membership.role.id,
      name: `${membership.user.first_name} ${membership.user.last_name}`.trim(),
      email: membership.user.email,
      phone: membership.user.phone_number || '',
      isPending: false,
    }));
    data.invited_users.forEach((invite) => {
      rows.push({
        id: invite.id,
        type: 'invited_user',
        role: invite.role,
        roleId: invite.project_role ?? undefined,
        name: invite.user
          ? `${invite.user.first_name} ${invite.user.last_name}`.trim()
          : 'Invited User (Pending)',
        email: invite.email,
        phone: invite.user?.phone_number || 'N/A',
        isPending: !invite.user,
      });
    });
    return rows;
  }, [data]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return teamMemberRows.filter(
      (row) =>
        (!query ||
          row.name.toLowerCase().includes(query) ||
          row.email.toLowerCase().includes(query) ||
          row.role.toLowerCase().includes(query)) &&
        (!roleFilter || row.role === roleFilter),
    );
  }, [roleFilter, searchQuery, teamMemberRows]);

  const uniqueRoles = useMemo(
    () => Array.from(new Set(teamMemberRows.map((row) => row.role))).sort(),
    [teamMemberRows],
  );

  const toggleUserSelection = useCallback((key: string) => {
    setSelectedUsers((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAllUsers = useCallback(() => {
    setSelectedUsers((current) =>
      current.size === filteredRows.length
        ? new Set()
        : new Set(filteredRows.map((row) => `${row.type}-${row.id}`)),
    );
  }, [filteredRows]);

  const mutationError =
    addUserMutation.error ||
    inviteInternalMutation.error ||
    inviteExternalMutation.error ||
    deleteUsersMutation.error;

  return {
    data,
    isLoading: teamQuery.isLoading,
    error:
      localError ||
      (mutationError as { data?: { error?: string; detail?: string }; message?: string } | null)
        ?.data?.error ||
      (mutationError as { data?: { detail?: string } } | null)?.data?.detail ||
      (mutationError as Error | null)?.message ||
      (teamQuery.error as Error | null)?.message ||
      null,
    success,
    setError,
    setSuccess,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    formType,
    setFormType,
    addInternalUserForm,
    setAddInternalUserForm,
    inviteInternalUserForm,
    setInviteInternalUserForm,
    inviteExternalUserForm,
    setInviteExternalUserForm,
    handleAddInternalUser,
    handleInviteInternalUser,
    handleInviteExternalUser,
    handleDeleteUsers,
    selectedUsers,
    toggleUserSelection,
    toggleAllUsers,
    teamMemberRows: filteredRows,
    uniqueRoles,
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    availableUsers: availableUsersQuery.data?.users ?? [],
    loadingUsers: availableUsersQuery.isLoading,
    isMutating:
      addUserMutation.isPending ||
      inviteInternalMutation.isPending ||
      inviteExternalMutation.isPending ||
      deleteUsersMutation.isPending,
    refetch: teamQuery.refetch,
    currentUser,
  };
};

type TradePartnersSnapshot = { previous?: TradePartnersResponse };

export const useTradePartners = (
  projectId: number,
  clientId: number,
  initialData?: TradePartnersResponse,
) => {
  const queryClient = useQueryClient();
  const key = projectTeamKeys.tradePartners(projectId, clientId);
  const query = useQuery({
    queryKey: key,
    queryFn: () => projectTeamApi.getTradePartners(projectId, clientId),
    initialData,
    staleTime: 30_000,
  });

  const snapshot = async (): Promise<TradePartnersSnapshot> => {
    await queryClient.cancelQueries({ queryKey: key });
    return { previous: queryClient.getQueryData<TradePartnersResponse>(key) };
  };
  const rollback = (
    _error: unknown,
    _variables: unknown,
    context?: TradePartnersSnapshot,
  ) => {
    if (context?.previous) queryClient.setQueryData(key, context.previous);
  };
  const settle = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    await queryClient.invalidateQueries({ queryKey: projectTeamKeys.all });
  };
  const updateApplication = (
    partnerId: number,
    application: TradePartnerApplication,
  ) => {
    queryClient.setQueryData<TradePartnersResponse>(key, (current) =>
      current
        ? {
            ...current,
            trade_partners: current.trade_partners.map((partner) =>
              partner.id === partnerId
                ? {
                    ...partner,
                    latest_application: application,
                    partner_client: application.partner_client,
                  }
                : partner,
            ),
          }
        : current,
    );
  };

  const createPartner = useMutation({
    mutationFn: (input: TradePartnerInput) =>
      projectTeamApi.createTradePartner(projectId, input),
    onMutate: snapshot,
    onSuccess: (partner) => {
      queryClient.setQueryData<TradePartnersResponse>(key, (current) =>
        current
          ? {
              ...current,
              trade_partners: [...current.trade_partners, partner].sort((a, b) =>
                a.company_name.localeCompare(b.company_name),
              ),
            }
          : current,
      );
    },
    onError: rollback,
    onSettled: settle,
  });

  const updatePartner = useMutation({
    mutationFn: ({ partnerId, input }: { partnerId: number; input: Partial<TradePartnerInput> }) =>
      projectTeamApi.updateTradePartner(projectId, partnerId, input),
    onMutate: snapshot,
    onSuccess: (partner) => {
      queryClient.setQueryData<TradePartnersResponse>(key, (current) =>
        current
          ? {
              ...current,
              trade_partners: current.trade_partners.map((item) =>
                item.id === partner.id ? partner : item,
              ),
            }
          : current,
      );
    },
    onError: rollback,
    onSettled: settle,
  });

  const deletePartner = useMutation({
    mutationFn: (partnerId: number) =>
      projectTeamApi.deleteTradePartner(projectId, partnerId, clientId),
    onMutate: async (partnerId): Promise<TradePartnersSnapshot> => {
      const context = await snapshot();
      queryClient.setQueryData<TradePartnersResponse>(key, (current) =>
        current
          ? {
              ...current,
              trade_partners: current.trade_partners.filter(
                (partner) => partner.id !== partnerId,
              ),
            }
          : current,
      );
      return context;
    },
    onError: rollback,
    onSettled: settle,
  });

  const createTrade = useMutation({
    mutationFn: (name: string) => projectTeamApi.createTrade(projectId, name, clientId),
    onMutate: snapshot,
    onSuccess: (trade: Trade) => {
      queryClient.setQueryData<TradePartnersResponse>(key, (current) =>
        current
          ? {
              ...current,
              trades: [
                ...current.trades.filter((item) => item.id !== trade.id),
                trade,
              ].sort((a, b) => a.name.localeCompare(b.name)),
            }
          : current,
      );
    },
    onError: rollback,
    onSettled: settle,
  });

  const resendApplication = useMutation({
    mutationFn: ({
      applicationId,
    }: {
      partnerId: number;
      applicationId: number;
    }) => projectTeamApi.resendTradePartnerApplication(projectId, applicationId),
    onMutate: snapshot,
    onSuccess: ({ application }, variables) =>
      updateApplication(variables.partnerId, application),
    onError: rollback,
    onSettled: settle,
  });

  return {
    ...query,
    partners: query.data?.trade_partners ?? [],
    trades: query.data?.trades ?? [],
    projectRoles: query.data?.project_roles ?? [],
    availableClients: query.data?.available_clients ?? [],
    createPartner,
    updatePartner,
    deletePartner,
    createTrade,
    resendApplication,
  };
};

export const useTradePartnerApplication = (token: string) => {
  const queryClient = useQueryClient();
  const key = projectTeamKeys.application(token);
  const query = useQuery({
    queryKey: key,
    queryFn: () => projectTeamApi.getTradePartnerApplication(token),
    enabled: Boolean(token),
    retry: false,
  });
  const submit = useMutation({
    mutationFn: (input: TradePartnerApplicationSubmitInput) =>
      projectTeamApi.submitTradePartnerApplication(input),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      return { previous: queryClient.getQueryData(key) };
    },
    onSuccess: (response) => queryClient.setQueryData(key, response),
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  return { ...query, application: query.data?.application ?? null, submit };
};

export const useProjectInvitation = (token: string) => {
  const queryClient = useQueryClient();
  const key = projectTeamKeys.invitation(token);
  const query = useQuery({
    queryKey: key,
    queryFn: () => projectTeamApi.getInvitationByToken(token),
    enabled: Boolean(token),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => projectTeamApi.acceptInvitation(token),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      return { previous: queryClient.getQueryData(key) };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  return { ...query, invitedUser: query.data?.invited_user ?? null, accept };
};
